import React, { useState, useEffect } from "react";
import { GridComponent, ColumnsDirective, ColumnDirective, Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport, Edit, Inject } from '@syncfusion/ej2-react-grids';
import { jsPDF } from "jspdf";
import Swal from "sweetalert2";
import 'sweetalert2/dist/sweetalert2.min.css';
import letterImage from "../data/letters.jpg";
import { MdDeleteForever , MdEditNote , MdDownloadForOffline } from "react-icons/md";
const Letters = () => {
  const [letters, setLetters] = useState([]);
  const [formData, setFormData] = useState({
    to: "",
    subject: "",
    dear: "",
    content: "",
    date: "",
  });
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false); // State to toggle form visibility

  // Fetch letters from the API
  useEffect(() => {
    const fetchLetters = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/letters`, {
          method: "GET",
          headers: {
            "api-key": process.env.REACT_APP_API_KEY,
          },
        });
  
        if (!response.ok) {
          throw new Error("Failed to fetch");
        }
  
        const data = await response.json();
  
        // Reverse the data array
        const reversedData = data.reverse();
  
        // Update the state with reversed data
        setLetters(reversedData);
      } catch (error) {
        Swal.fire("Error", "Failed to fetch letters. Please try again later.", "error");
      }
    };
  
    fetchLetters();
  }, []); // Dependency array to run the effect only once
  

  // Handle input change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Submit form data (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    const action = editingId ? "Update" : "Create";
    const confirm = await Swal.fire({
      title: `${action} Letter?`,
      text: `Are you sure you want to ${action.toLowerCase()} this letter?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${action.toLowerCase()} it!`,
    });

    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      const url = editingId
        ? `${process.env.REACT_APP_API_BASE_URL}/api/letters/${editingId}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/letters`;

      const method = editingId ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "api-key": process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify(formData),
      });

      const updatedLetter = await response.json();

      if (editingId) {
        setLetters((prev) =>
          prev.map((letter) => (letter._id === editingId ? updatedLetter : letter))
        );
        Swal.fire("Updated!", "Letter updated successfully.", "success");
      } else {
        setLetters((prev) => [...prev, updatedLetter]);
        Swal.fire("Created!", "Letter created successfully.", "success");
      }

      setFormData({ to: "", subject: "", dear: "", content: "", date: "" });
      setEditingId(null);
      setShowForm(false); // Hide the form after submission
    } catch (error) {
      Swal.fire("Error", `Failed to ${action.toLowerCase()} the letter. Please try again.`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Generate PDF with background
  const generatePDF = (letter) => {
    // Enable compression
    const pdf = new jsPDF({ compress: true });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
  
    // Add the optimized background image
    pdf.addImage(letterImage, "JPEG", 0, 0, pageWidth, pageHeight, undefined, "FAST");
  
    // Add letter details
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
  
    const formattedDate = new Date(letter.date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  
    pdf.text(`Date: ${formattedDate}`, 20, 22); 
    pdf.text(`To: ${letter.to}`, 20, 60, { maxWidth: 50 }); 
    pdf.text(`Subject: ${letter.subject}`, 20, 90, { maxWidth: 120 });
    pdf.text(`Dear ${letter.dear},`, 20, 110);
    pdf.text(letter.content, 20, 120, { maxWidth: 170 }); 
  
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "italic");
    pdf.text(`Generated by: Vaaar-management/${letter._id}`, 5, pageHeight - 24);
  
    // Save the PDF
    pdf.save(`${letter.subject}.pdf`);
  };
  
  // Delete a letter
  const deleteLetter = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Letter?",
      text: "Are you sure you want to delete this letter?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/letters/${id}`, {
        method: "DELETE",
        headers: {
          "api-key": process.env.REACT_APP_API_KEY,
        },
      });
      setLetters((prev) => prev.filter((letter) => letter._id !== id));
      Swal.fire("Deleted!", "Letter has been deleted.", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to delete the letter. Please try again.", "error");
    }
  };

  // Edit a letter
  const editLetter = (letter) => {
    const formattedDate = new Date(letter.date).toISOString().split('T')[0];
    setFormData({
      to: letter.to,
      subject: letter.subject,
      dear: letter.dear,
      content: letter.content,
      date: formattedDate,
    });
    setEditingId(letter._id);
    setShowForm(true); // Show form in edit mode
  };

  // Toggle create form visibility
  const toggleCreateForm = () => {
    setFormData({
      to: "",
      subject: "",
      dear: "",
      content: "",
      date: "",
    });
    setEditingId(null);
    setShowForm(true); // Show form in create mode
  };
  
  const lettersGrid = [
  
    {
      field: 'subject',
      headerText: 'Subject',
      width: '150',
      textAlign: 'Center',
    },
    {
      field: 'to',
      headerText: 'To',
      width: '150',
      textAlign: 'Center',
    },
    {
      field: 'date',
      headerText: 'Date',
      width: '100',
      textAlign: 'Center',
    },
    {
        field: 'dear',
        headerText: 'Respect TO',
        width: '100',
        textAlign: 'Center',
      },
    
    {
      field: 'content',
      headerText: 'Message',
      textAlign: 'Center',
      width: '150',
    },
    {
      field: 'updatedAt',
      headerText: 'Updated At',
      width: '100',
      textAlign: 'Center',
    },
    {
      field: '_id',
      headerText: 'Actions',
      width: '150',
      textAlign: 'Center',
      template: (props) => (
        <div>
          <button 
            className="bg-blue-500 text-white px-1 py-1 text-xl rounded mr-2"
            onClick={() => editLetter(props)}
          >
            <MdEditNote/>
          </button>
          <button
            className="bg-red-500 text-white px-1 py-1 text-xl rounded mr-2"
            onClick={() => {
              Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!',
              }).then(async (result) => {
                if (result.isConfirmed) {
                  try {
                    await deleteLetter(props._id);

                    Swal.fire({
                      title: 'Deleted!',
                      text: 'Letter has been deleted.',
                      icon: 'success',
                      confirmButtonText: 'OK',
                    //   confirmButtonColor: '#3085d6',
                    });
                  } catch (error) {
                    Swal.fire({
                      title: 'Error!',
                      text: 'Failed to delete the letter or associated file.',
                      icon: 'error',
                      confirmButtonText: 'OK',
                    //   confirmButtonColor: '#3085d6',
                    });
                  }
                }
              });
            }}
          >
            <MdDeleteForever />
          </button>
          <button 
            className="bg-green-400 text-white px-1 py-1 text-xl rounded mr-2"
            onClick={() => generatePDF(props)}
          >
          <MdDownloadForOffline />
          </button>
        </div>
      )
    },
  ];

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6 text-center dark:text-white text-gray-800">Letter Management</h1>

      {/* Buttons to toggle form visibility */}
      <div className="text-center mb-4">
        <button
          onClick={toggleCreateForm}
          className="bg-blue-500 text-white px-2 py-1 rounded-md text-md hover:bg-blue-600"
        >
    Create Letter
        </button>
      </div>

      {/* Show the form when 'showForm' is true */}
      {showForm && (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex  justify-center items-center">
      <div className="bg-white p-8 rounded-xl shadow-2xl w-[600px] h-4/5 overflow-y-auto">
        <h2 className="text-2xl font-semibold mb-6 text-gray-800 border-b pb-2">
          {editingId ? "Edit Letter" : "Create Letter"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* To Field */}
          <div>
            <label htmlFor="to" className="block text-lg font-semibold text-gray-700 mb-2">
              To
            </label>
            <input
              id="to"
              type="text"
              name="to"
              value={formData.to}
              onChange={handleChange}
              required
              className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter recipient's name"
            />
          </div>
    
          {/* Subject Field */}
          <div>
            <label htmlFor="subject" className="block text-lg font-semibold text-gray-700 mb-2">
              Subject
            </label>
            <input
              id="subject"
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter subject"
            />
          </div>
    
          {/* Dear Field */}
          <div>
            <label htmlFor="dear" className="block text-lg font-semibold text-gray-700 mb-2">
              Dear
            </label>
            <input
              id="dear"
              type="text"
              name="dear"
              value={formData.dear}
              onChange={handleChange}
              required
              className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter recipient's title (e.g., Sir/Madam)"
            />
          </div>
    
          {/* Content Field */}
          <div>
            <label htmlFor="content" className="block text-lg font-semibold text-gray-700 mb-2">
              Content
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              required
              className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter content of the letter"
              rows="6"
            ></textarea>
          </div>
    
          {/* Date Field */}
          <div>
            <label htmlFor="date" className="block text-lg font-semibold text-gray-700 mb-2">
              Date
            </label>
            <input
              id="date"
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
              className="border border-gray-300 p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
    
          {/* Form Buttons */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg transition duration-200 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg transition duration-200 font-medium"
            >
              {editingId ? "Update Letter" : "Create Letter"}
            </button>
          </div>
        </form>
      </div>
    </div>
    
     
      )}
   
      <GridComponent
        id="gridcomp"
        dataSource={letters}
        allowPaging
        allowSorting
       
      >
        <ColumnsDirective>
          {lettersGrid.map((item, index) => (
            <ColumnDirective key={index} {...item} />
          ))}
        </ColumnsDirective>
        <Inject services={[Resize, Sort, ContextMenu, Filter , Page, ExcelExport, Edit, PdfExport]} />
      </GridComponent>
    </div>
  );
};

export default Letters;
